<template>
  <div class="board-list-card-wrap">
    <a
      v-click-outside="clickOutsideHandler"
      v-observe-visibility="{
        callback: visibilityChanged,
        throttle: 200,
        throttleOptions: {
          leading: 'visible',
        },
      }"
      v-draghover="'board-list-card-wrap__dragging-on'"
      class="board-list-card-wrap pt border-none border-t-0 no-underline"
      :class="{
        'drop-target': cardIsDropTarget,
        cardHover: isCardForEdit || showEditTitle,
        hover: cardHover,
        'editingTitle border-important-focusedBorder': showEditTitle,
        'border-important-border': !showEditTitle,
        [childStyleClasses]: isChild,
      }"
      :href="cardLink"
      @dragover="dragOverHandler"
      @drop="handleDrop"
      @dragenter="handleDragEnter"
      @click="cardClickHandler"
      @mousedown="isEditable"
    >
      <div
        v-if="draggingFile && visible"
        class="board-list-card-drop-target"
        :class="{ 'small-target': cardHeight < 95 }"
        @dragleave="handleDragLeave"
      >
        <em
          :style="getCustomIconStyle(iconPath('upload-cloud'))"
          class="custom-icons"
        />
        <span class="drop-files-label">{{ translate('dropFilesLabel') }}</span>
      </div>
      <div
        :ref="'list-card-' + cardId"
        class="list-card"
        @mouseover="mouseOverHandler"
        @mousemove="mouseMoveHandler"
        @mouseleave="mouseLeaveHandler"
      >
        <div
          v-if="displayCardIcons"
          class="flex items-center h-full"
        >
          <DragHandle
            v-if="!isMobile && isLayoutListOrTimeline && !forceHideDragHandle"
            :class="{ invisible: showDragHandle || (readOnly && !readOnlyAllowDragCards) }"
          />
          <ChildBranchIcons
            :depth="depth"
            :depth-siblings="depthSiblings"
            :is-child="isChild"
            :last-item="lastItem"
          />
          <ToggleExpandButton
            ref="childCardsWrap"
            class="w-6 h-6 p-0 hover:bg-icon-bg"
            :class="{
              invisible: !hasChildCards || ancestorsCardIds.includes(cardId),
              'bg-icon-bg': isChildCardsShown,
              'mr-2': !isChild,
            }"
            :expanded="isChildCardsShown"
            @click.stop.prevent="onToggleChildCards"
          />
          <div
            v-if="isChild || isViewOrMyWork"
            class="hover:bg-hover-onHover rounded mr-1"
            :class="{ 'ml-3': isViewOrMyWork && isChild }"
          >
            <v-popover
              :key="'status-picker-popover'"
              :ref="CardPopupRef.Status"
              interactive
              tag="div"
              content-tag="div"
              :trigger="'click'"
              :role="'poper'"
              :append-to="bodyRef"
              :on-trigger="stopTriggerPropagation"
              :on-untrigger="stopTriggerPropagation"
              :on-show="() => !cancelledOrCompletedSprint && !isGuestReader"
              @state="statusPickerStateChange"
            >
              <div
                v-tooltip="{
                  content: card.list_title,
                  placement: 'top',
                  onShow: () => !isMobile,
                }"
                class="w-6 h-6"
                :style="listIconStyle"
              />
              <template #content="{ state, hide }">
                <ListCardStatusPicker
                  v-if="state.isVisible"
                  :custom-statuses-list="lists"
                  :selected-status="selectedStatus"
                  :show-dropdown-label="false"
                  :allow-color-change="false"
                  @click="($event) => handleStatusItemClick($event, hide)"
                  @close="hide"
                  @esc="hide"
                />
              </template>
            </v-popover>
          </div>
        </div>

        <div class="preview-card-header">
          <ParentCardTitlePreview
            v-if="showParentCardTitlePreview"
            :key="parentCard.card_id"
            :title="parentCardTitlePreviewText"
            :icon="parentCardTitlePreviewIcon"
            :tooltip="parentCardTitlePreviewTooltip"
            class="parent-card usn"
          />
          <div
            :class="[
              { 'status-icon': !groupByStatus },
              { 'list-layout-status-icon': !isLayoutDefault },
              { 'mt-2.5': !hasParentCard && isLayoutDefault },
            ]"
          >
            <div
              v-if="!groupByStatus && !isChild && !isViewOrMyWork"
              class="hover:bg-hover-onHover rounded p-1 -my-0.5 -mx-1"
            >
              <v-popover
                :key="'status-picker-popover'"
                :ref="CardPopupRef.Status"
                interactive
                tag="div"
                content-tag="div"
                :trigger="'click'"
                :role="'poper'"
                :append-to="bodyRef"
                :on-trigger="stopTriggerPropagation"
                :on-untrigger="stopTriggerPropagation"
                :on-show="() => !cancelledOrCompletedSprint"
                @state="statusPickerStateChange"
              >
                <div
                  v-tooltip="{
                    content: card.list_title,
                    placement: 'top',
                    onShow: () => !isMobile,
                  }"
                  class="status w-4 h-4 min-w-[16px]"
                  :style="{
                    ...getCustomIconStyle(iconPath(listIcon)),
                    backgroundColor: listIconColor,
                  }"
                />
                <template #content="{ state, hide }">
                  <ListCardStatusPicker
                    v-if="state.isVisible"
                    :custom-statuses-list="lists"
                    :selected-status="selectedStatus"
                    :show-dropdown-label="false"
                    :allow-color-change="false"
                    @click="($event) => handleStatusItemClick($event, hide)"
                    @close="hide"
                    @esc="hide"
                  />
                </template>
              </v-popover>
            </div>
            <div
              v-if="!showEditTitle"
              class="board-item-card-title"
              :class="[
                { 'mt-1': hasParentCard && isLayoutDefault && groupByStatus },
                { 'pl-2': isLayoutListOrTimeline && !groupByStatus && !isViewOrMyWork },
              ]"
            >
              <div class="title">
                <v-popover
                  v-if="showMainStatusPicker && !isChild"
                  :key="'status-picker-popover'"
                  :ref="CardPopupRef.Status"
                  interactive
                  tag="div"
                  content-tag="div"
                  :trigger="'manual'"
                  :role="'poper'"
                  :append-to="bodyRef"
                  :on-trigger="stopTriggerPropagation"
                  :on-untrigger="stopTriggerPropagation"
                  :on-show="() => !cancelledOrCompletedSprint"
                  :offset="[0, 20]"
                  @state="statusPickerStateChange"
                >
                  <template #content="{ state, hide }">
                    <ListCardStatusPicker
                      v-if="state.isVisible"
                      :custom-statuses-list="lists"
                      :selected-status="selectedStatus"
                      :show-dropdown-label="false"
                      :allow-color-change="false"
                      @click="($event) => handleStatusItemClick($event, hide)"
                      @close="hide"
                      @esc="hide"
                    />
                  </template>
                </v-popover>
                <v-popover
                  v-if="isSelectedCard || keybSelectedCard || sprintPickerOpen"
                  :key="'sprint-picker-popover'"
                  :ref="CardPopupRef.Sprint"
                  interactive
                  tag="div"
                  content-tag="div"
                  :role="'poper'"
                  :trigger="'manual'"
                  :append-to="bodyRef"
                  :on-trigger="stopTriggerPropagation"
                  :on-untrigger="stopTriggerPropagation"
                  :offset="[0, 20]"
                  @state="sprintPickerStateChange"
                >
                  <template #content="{ state, hide }">
                    <ListDropdown
                      v-if="state.isVisible"
                      :items="sprintPickerOptions"
                      :selected-item="selectedCardSprint"
                      :width="170"
                      @click="($event) => handleSprintItemClick($event, hide)"
                      @closeEscape="hide"
                    />
                  </template>
                </v-popover>
                <ResourceIcon
                  v-if="isEpic && !isGuestReader"
                  :src="card.icon?.src || $cons.epicDefaultIcon"
                  :color="card.icon?.color"
                  :type="card.icon?.type"
                  class="mr-1"
                />
                {{ card.title }}
              </div>
            </div>
            <div
              v-if="showEditTitle"
              class="edit-card-title-wrap"
              @wheel.stop.prevent
              @touchmove.stop.prevent
              @scroll.stop.prevent
              @click.stop
              @mouseup.stop
              @mousedown.stop
            >
              <vue-scroll
                :ops="{
                  scrollPanel: { scrollingX: false },
                  rail: { size: '8px', gutterOfSide: '4px', gutterOfEnds: '4px' },
                  bar: { size: '8px', keepShow: true },
                }"
                class="title-input"
              >
                <AutoExpandTextarea
                  ref="cardTitleInput"
                  :value="tempTitle"
                  class="edit-card-title edit"
                  :class="{ 'show-status': !groupByStatus }"
                  :text-value="tempTitle"
                  :focus-field="true"
                  :textarea-class="'edit-card-title'"
                  :initial-height="textareaHeight"
                  contenteditable="true"
                  @click.stop.prevent
                  @input="tempTitle = $event.target.value"
                  @enter="saveTitle"
                  @esc="hideEditMode"
                  @mousedown="focusElementHandler"
                  @mouseup="blurElementHandler"
                />
              </vue-scroll>
            </div>
          </div>
        </div>
        <div
          v-if="!showEditTitle"
          class="preview-card-footer z-10"
        >
          <div
            v-if="
              ((!localOnlyCard && cardHover && !showEditTitle) || (isCardForEdit && cardHover)) &&
              !readOnly
            "
          >
            <div
              class="absolute"
              :class="{
                'top-3 right-3 mr-6': isLayoutDefault,
                'top-2 left-2': isLayoutListOrTimeline,
              }"
            >
              <div
                v-if="!isEpic"
                class="btn-square border border-solid border-important-border w-6 h-6 px-0 hover:bg-important-hover bg-important-bg group"
                :class="{ 'rounded-r-none': isLayoutDefault }"
                @click="handleInlineRenameButton(cardId)"
              >
                <em
                  :style="getCustomIconStyle(iconPath('edit'))"
                  class="custom-icons action-icon-small-card bg-icon-default group-hover:bg-icon-hover"
                />
              </div>
            </div>
            <v-popover
              ref="actionsButton"
              key="actions-button"
              interactive
              tag="div"
              content-tag="div"
              :placement="'right-start'"
              :role="'poper'"
              :trigger="'click'"
              :append-to="bodyRef"
              :get-reference-client-rect="getActionsButtonReferenceClientRect"
              :on-trigger="stopTriggerPropagation"
              :on-untrigger="stopTriggerPropagation"
              :on-show="toggleActionsDropdown"
              :on-hide="hideActionsDropdown"
              :duration="0"
              @state="actionsButtonStateChange"
            >
              <CardActionsButton
                ref="actionsButton"
                class="absolute"
                :class="{
                  'active-dropdown': isCardForEdit,
                  'top-3 right-3 mr-px': isLayoutDefault,
                  'rounded-l-none': isLayoutDefault && !isEpic,
                  'top-2 right-0.5': isLayoutListOrTimeline,
                }"
              />
              <template #content="{ state, hide }">
                <BoardListCardActions
                  v-if="state.isVisible"
                  :card="card"
                  :card-list-id="listId"
                  @close-tippy="hide"
                />
              </template>
            </v-popover>
          </div>
          <v-popover
            v-if="!isLayoutListOrTimeline"
            ref="tagsWrapPopover"
            key="tags-popover"
            interactive
            tag="div"
            content-tag="div"
            :role="'poper'"
            :trigger="'manual'"
            :append-to="bodyRef"
          >
            <template #default>
              <div
                ref="tagsSectionWrap"
                class="tag-section flex flex-column flex-wrap max-h-14 content-start mb-2"
                :class="{ 'h-0': !showTags }"
              >
                <CardTag
                  v-for="tag in cardTags.slice(0, maxTagsForShow)"
                  :ref="`tagWrap-${tag.id}`"
                  :key="tag.id"
                  :name="tag.name"
                  :show-name="true"
                  :color="tag.color"
                  :tooltip="false"
                />
                <span
                  v-if="cardTags.length > maxTagsForShow"
                  class="flex items-center justify-center text-deEmphasised-text text-xs leading-3"
                >
                  +{{ cardTags.length - maxTagsForShow }}
                </span>
              </div>
            </template>
            <template #content="{ state, hide }">
              <CardTagsPopupWrapper
                v-if="state.isVisible"
                :card-id="cardId"
                @closePopup="hide"
                @close-tippy="hide"
                @esc="hide"
              />
            </template>
          </v-popover>
          <v-popover
            v-if="renderTagsTooltip"
            ref="tagsTooltipPopover"
            key="tags-popover"
            tag="div"
            content-tag="div"
            :trigger="'mouseenter'"
            :offset="[0, 4]"
            :placement="'top-start'"
            :on-show="() => renderTagsTooltip"
            :trigger-target="tagsTooltipTarget()"
            :get-reference-client-rect="getTagsReferenceClientRect"
          >
            <template #content="{ state }">
              <TagsPopoverContent
                v-if="state.isVisible"
                class="small-card-tags-popover"
                :card-tags="cardTags"
              />
            </template>
          </v-popover>
          <div
            v-if="visible"
            class="card-icons-col"
          >
            <div
              v-if="displayCardIcons"
              class="flex col-items flex-row-reverse"
            >
              <v-popover
                ref="membersPopoverRef"
                key="members-popover"
                interactive
                tag="div"
                content-tag="div"
                :append-to="bodyRef"
                :role="'poper'"
                :trigger="'click'"
                :on-trigger="onMembersTrigger"
                :on-show="onMembersShow"
                :on-hide="onMembersHide"
                :on-untrigger="stopTriggerPropagation"
              >
                <CardMembers
                  :id="`${cardId}-${depth}`"
                  :card-id="cardId"
                  :list-id="listId"
                  :class="{ 'items-center p-0': isMobile }"
                  class="members-item btn-gray bg-transparent w-20 items-stretch flex-nowrap hover:bg-hover-onHover"
                />
                <template #content="{ state, hide }">
                  <MembersDropdownList
                    v-if="state.isVisible"
                    :entity="card"
                    :members="currentCardMembers"
                    :resource-type="card.type || ResourceType.Card"
                    @close-dropdown="hide"
                  />
                </template>
              </v-popover>
              <CardDate
                :id="`${cardId}-${depth}`"
                ref="dueDateWrap"
                :card-id="cardId"
                :list-id="listId"
                :card-hover="cardHover"
                :visible="visible"
                :layout-prop="layout"
                :class="
                  isLayoutTimeline || sprintSidebar
                    ? 'absolute top-0 w-0 h-0'
                    : 'btn-gray bg-transparent w-[136px] px-0 pl-2 items-stretch flex-nowrap justify-start hover:bg-hover-onHover'
                "
              />
              <v-popover
                v-if="isSelectedCard || startDatePopupShown"
                ref="startDateWrap"
                key="start-date-popover"
                interactive
                tag="div"
                content-tag="div"
                trigger="click"
                placement="left-start"
                :role="'poper'"
                :append-to="bodyRef"
                :on-show="() => cardHover || $store.getters.selectedCardId === cardId"
                :on-hide="() => (startDatePopupShown = false)"
                :on-trigger="stopTriggerPropagation"
                :on-untrigger="stopTriggerPropagation"
              >
                <template #content="{ state, hide }">
                  <DateTimePicker
                    v-if="state.isVisible"
                    :current-card-id="cardId"
                    :selected-date="card.start_date"
                    :property="'start_date'"
                    @esc="hide"
                    @close-popover="hide"
                    @dueDate="(date, rem, prop, cId) => setCardDate(date, rem, prop, cId) && hide()"
                  />
                </template>
              </v-popover>

              <CardTags
                :id="`${cardId}-${depth}`"
                ref="tagsWrap"
                :card-id="cardId"
                :list-id="listId"
                class="tags-item btn-gray bg-transparent items-stretch flex-nowrap justify-start hover:bg-hover-onHover"
                :class="[isMobile ? 'w-16 p-0' : `${isLayoutList ? 'w-24' : 'w-32'}`]"
                :card-hover="cardHover"
              />
              <CardCustomSection
                v-if="!isMobile && showPrioritySection"
                :key="`priority-${cardId}`"
                ref="priorityWrap"
                class="priority-icon"
                :card="card"
                :list-id="listId"
                :items="priorityOptions"
                :keyword="'priority'"
                :is-list-card="true"
                :disabled="readOnly"
                :popover-offset="[0, 8]"
                @dropdownStateChange="priorityWrapDropdownChange"
              />
              <CardCustomSection
                v-if="!isMobile && showEstimateSection"
                ref="estimateWrap"
                :key="`estimate-${cardId}`"
                class="estimate-icon"
                :card="card"
                :list-id="listId"
                :card-hover="cardHover"
                :items="getEstimateOptions(card.project_id)"
                :keyword="'estimate'"
                :default-tooltip-label="'estimated'"
                :is-list-card="true"
                :default-icon="estimateDefaultIcon"
                :null-value-tooltip-label="translate('setEstimate')"
                :disabled="estimateSectionDisabled"
                :popover-offset="[0, 8]"
                :is-quick-card-modal="false"
                @dropdownStateChange="estimateWrapDropdownChange"
              />
              <CardProject
                v-if="showCardProject"
                :project-id="card.project_id"
              />
            </div>
            <template v-else>
              <!-- kanban layout icons -->
              <div
                v-if="isCardAddedToEpic && !inEpicBoard && !isGuestReader"
                v-tooltip="{
                  content: epicsTitle,
                  onShow: () => !isMobile,
                }"
                class="mr-1 w-6 h-6 flex items-center justify-center rounded border border-solid border-standard-border"
              >
                <ResourceIcon
                  :src="epicsIcon.src || $cons.epicDefaultIcon"
                  :color="epicsIcon.color"
                  :type="epicsIcon.type"
                />
              </div>
              <v-popover
                ref="membersPopoverRef"
                key="members-popover"
                interactive
                tag="div"
                content-tag="div"
                :append-to="bodyRef"
                :role="'poper'"
                :trigger="'click'"
                :on-trigger="onMembersTrigger"
                :on-show="onMembersShow"
                :on-hide="onMembersHide"
                :on-untrigger="stopTriggerPropagation"
              >
                <CurrentModelMembers
                  ref="membersWrap"
                  :members="sortedMembers"
                  :avatar-size="'small-xs'"
                  :is-card-preview="true"
                  :new-member-icon-class="'small-icon'"
                  :disabled="readOnly"
                  class="card-view-members-wrap"
                />
                <template #content="{ state, hide }">
                  <MembersDropdownList
                    v-if="state.isVisible"
                    :entity="card"
                    :members="currentCardMembers"
                    :resource-type="card.type || ResourceType.Card"
                    @close-dropdown="hide"
                  />
                </template>
              </v-popover>
              <CardCustomSection
                v-if="showEstimateSection"
                :key="`estimate-${cardId}`"
                ref="estimateWrap"
                :card="card"
                :list-id="listId"
                :items="getEstimateOptions(card.project_id)"
                :keyword="'estimate'"
                :default-tooltip-label="'estimated'"
                :default-icon="estimateDefaultIcon"
                :null-value-tooltip-label="translate('setEstimate')"
                :is-quick-card-modal="false"
                :class="{
                  'invisible w-0 h-0': !cardsProjectHasEstimateSystem || hasNoEstimate,
                }"
                :disabled="readOnly"
                :popover-offset="[0, 2]"
                @dropdownStateChange="estimateWrapDropdownChange"
              />
              <!-- due date popup -->
              <v-popover
                v-if="isDueDateSet || isSelectedCard || dueDatePopupShown"
                ref="dueDateWrap"
                key="due-date-popover"
                interactive
                tag="div"
                content-tag="div"
                placement="bottom-start"
                trigger="click"
                :role="'poper'"
                :on-trigger="stopTriggerPropagation"
                :on-untrigger="stopTriggerPropagation"
                :append-to="bodyRef"
                :on-show="() => !isGuestReader"
                @state="(state) => (dueDatePopupShown = state.isVisible)"
              >
                <v-popover
                  tag="div"
                  content-tag="div"
                  :append-to="bodyRef"
                  :on-show="() => isSelectedCard && !isGuestReader"
                  :delay="[300, 0]"
                >
                  <CardDateContent
                    v-if="isDueDateSet"
                    :due-date-class="dueDateClass"
                    :due-date-text="dueDateText"
                    :calendar-icon="getCustomIconStyle(iconPath('calendar'))"
                    :small-card="true"
                  />
                  <template #content="{ state }">
                    <DueDateTooltip
                      v-if="state.isVisible"
                      :due-date="card.due_date"
                    />
                  </template>
                </v-popover>
                <template #content="{ state, hide }">
                  <DateTimePicker
                    v-if="state.isVisible"
                    :current-card-id="cardId"
                    :selected-date="card.due_date"
                    :property="'due_date'"
                    @esc="hide"
                    @close-popover="hide"
                    @dueDate="(date, rem, prop, cId) => setCardDate(date, rem, prop, cId) && hide()"
                  />
                </template>
              </v-popover>
              <!-- start date popup -->
              <v-popover
                v-if="isSelectedCard || startDatePopupShown"
                ref="startDateWrap"
                key="start-date-popover"
                interactive
                tag="div"
                content-tag="div"
                trigger="click"
                :placement="isLayoutListOrTimeline ? 'left-start' : 'bottom-start'"
                :role="'poper'"
                :append-to="bodyRef"
                :on-show="() => cardHover || $store.getters.selectedCardId === cardId"
                :on-hide="() => (startDatePopupShown = false)"
                :on-trigger="stopTriggerPropagation"
                :on-untrigger="stopTriggerPropagation"
              >
                <template #content="{ state, hide }">
                  <DateTimePicker
                    v-if="state.isVisible"
                    :current-card-id="cardId"
                    :selected-date="card.start_date"
                    :property="'start_date'"
                    @esc="hide"
                    @close-popover="hide"
                    @dueDate="(date, rem, prop, cId) => setCardDate(date, rem, prop, cId) && hide()"
                  />
                </template>
              </v-popover>
              <RelationsMeta
                v-if="isBlockedByOtherCard"
                :cards="blockedByCards"
                :relation-type="LinkedCardType.BlockedBy"
                :render-tooltip="cardHover"
              />
              <RelationsMeta
                v-if="isBlockingOtherCard"
                :cards="blockingCards"
                :relation-type="LinkedCardType.Blocks"
                :render-tooltip="cardHover"
              />
              <CardCustomSection
                v-if="showPrioritySection"
                :key="`priority-${cardId}`"
                ref="priorityWrap"
                :card="card"
                :list-id="listId"
                :items="priorityOptions"
                :keyword="'priority'"
                :class="{ 'invisible w-0 h-0': hasNoPriority }"
                :disabled="readOnly"
                :popover-offset="[0, 2]"
                @dropdownStateChange="priorityWrapDropdownChange"
              />
            </template>
            <div
              v-if="!isLayoutDefault"
              class="flex"
            >
              <div
                v-for="section in metaDataSections"
                :key="section.keyword"
                v-tooltip="getTooltipIconSection(section.keyword)"
                class="footer-icon-wrap"
              >
                <em
                  :style="getCustomIconStyle(iconPath(section.icon))"
                  class="custom-icons small-card-info-icons"
                  :class="section.extraClass || ''"
                />
                <span class="total-count">
                  {{ section.value }}
                </span>
              </div>
              <RelationsMeta
                v-if="isBlockedByOtherCard && isLayoutList"
                :cards="blockedByCards"
                :relation-type="LinkedCardType.BlockedBy"
                :render-tooltip="cardHover"
              />
              <RelationsMeta
                v-if="isBlockingOtherCard && isLayoutList"
                :cards="blockingCards"
                :relation-type="LinkedCardType.Blocks"
                :render-tooltip="cardHover"
              />
            </div>
            <div
              v-for="section in metaDataSections"
              v-else
              :key="section.keyword"
              v-tooltip="getTooltipIconSection(section.keyword)"
              class="footer-icon-wrap"
            >
              <em
                :style="getCustomIconStyle(iconPath(section.icon))"
                class="custom-icons small-card-info-icons"
                :class="section.extraClass || ''"
              />
              <span class="total-count">
                {{ section.value }}
              </span>
            </div>
            <div
              v-if="hasChildCards && (!isLayoutList || shiftHoverCardPreview)"
              v-tooltip="{
                content: childCardsTooltip,
                placement: 'bottom',
                onShow: () => !isMobile,
              }"
              class="child-card-preview-button"
            >
              <ChildCardsPreviewButton
                :cards-count="childCardsCount"
                :done-cards-count="doneCardsCount"
                :started-cards-count="startedCardsCount"
                :preview-opened="isChildCardsShown"
                @click="onToggleChildCards"
              />
            </div>
          </div>
        </div>
        <ActionButtons
          v-if="showEditTitle"
          class="card-footer-buttons"
          :stop-confirm-propagation="true"
          :stop-cancel-propagation="true"
          @confirm="saveTitle"
          @cancel="hideEditMode"
        />
      </div>
      <ChildCardsPreview
        v-if="isChildCardsShown && hasChildCards && isLayoutDefault"
        :cards-ids="notArchivedCards"
        :parent-card-id="cardId"
      />
    </a>
    <CardSubCards
      v-if="isChildCardsShown && hasChildCards && isLayoutListOrTimeline"
      :cards-ids="notArchivedCards"
      :parent-card-id="cardId"
      :ancestors-card-ids="ancestorsCardIds"
      :depth="depth + 1"
      :depth-siblings="depthSiblings"
      :layout="layout"
    />
    <Portal
      v-if="moveCardToCompletedSprintDialog.isRevealed.value"
      to="root-widget"
    >
      <ConfirmationModal
        :title="translate('moveToCompletedSprint')"
        :description="translate('onlyDoneCardsCanBeMovedToCompletedSprint')"
        :confirm-button-text="translate('move')"
        @cancel="moveCardToCompletedSprintDialog.cancel()"
        @confirm="moveCardToCompletedSprintDialog.confirm()"
      ></ConfirmationModal>
    </Portal>
  </div>
</template>

<script>
import { LinkedCardType, SprintState, ListBehavior } from '@superthread-com/api';
import CardTagsPopupWrapper from '@/components/widgets/CardTagsPopupWrapper.vue';
import CardTag from '@/components/elements/CardTag';
import boardListCardMixin from '@/mixins/boardListCardMixin';
import AutoExpandTextarea from '@/components/elements/AutoExpandTextarea';
import ChildCardsPreview from '@/components/widgets/ChildCardsPreview';
import ChildCardsPreviewButton from '@/components/widgets/ChildCardsPreviewButton.vue';
import CardSubCards from '@/components/elements/cards/CardSubCards';
import filesReadUpload from '@/mixins/filesReadUpload';
import isEditable from '@/mixins/isEditable';
import EventBus from '@/utilities/eventBus';
import customIconsMixin from '@/mixins/customIconsMixin';
import styleVariables from '@/assets/scss/variables.module.scss';
import isInViewport from '@/utilities/isInViewport';
import { convertStyleToNumber, getSmallCardTagMaxWidth } from '@/utilities/rootStyles';
import elementFocusMixin from '@/mixins/elementFocusMixin';
import CardDate from '@/components/elements/cards/CardDate';
import CardTags from '@/components/elements/cards/CardTags';
import CardMembers from '@/components/elements/cards/CardMembers';
import CardActionsButton from '@/components/elements/cards/CardActionsButton';
import CardProject from '@/components/elements/cards/CardProject';
import ToggleExpandButton from '@/components/elements/common/ToggleExpandButton';
import DragHandle from '@/components/elements/common/DragHandle';
import CurrentModelMembers from './CurrentModelMembers';
import cardStatuses from '@/mixins/cardStatuses';
import childCardCounterMixin from '@/mixins/childCardCounterMixin';
import CardDateContent from '@/components/functional/CardDateContent';
import ParentCardTitlePreview from './ParentCardTitlePreview';
import CardCustomSection from '@/components/elements/cards/CardCustomSection';
import estimateOptions from '@/mixins/estimateOptions';
import { groupByOption } from '@/types/groupByOption';
import BoardListCardActions from '@/components/widgets/BoardListCardActions';
import MembersDropdownList from '@/components/widgets/MembersDropdownList';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';
import ChildBranchIcons from './ChildBranchIcons.vue';
import TagsPopoverContent from '@/components/elements/TagsPopoverContent.vue';
import { ref, getCurrentInstance, computed } from 'vue';
import ResourceIcon from '@/components/elements/common/ResourceIcon';
import { ResourceType } from '@/types/resources';
import ActionButtons from '@/components/widgets/ActionButtons.vue';
import useManageCardMembers from '@/boards/composables/useManageCardMembers';
import DueDateTooltip from '@/components/tooltips/DueDateTooltip.vue';
import dueDateMixin from '@/mixins/dueDateMixin';
import RelationsMeta from '@/components/elements/cards/RelationsMeta.vue';
import usePriorityOptions from '@/utilities/composables/usePriorityOptions';
import ListCardStatusPicker from '@/components/widgets/ListCardStatusPicker';
import useBoardLists from '@/utilities/composables/useBoardLists';
import moveCardMixin from '@/mixins/moveCardMixin';
import useCheckSprintState from '@/utilities/composables/useCheckSprintState';
import { getSprintListKey, getSprintKey } from '@/utilities/sprintKeys';
import useSprintPicker from '@/utilities/composables/useSprintPicker';
import ListDropdown from '@/components/widgets/ListDropdown';
import useMoveToSprint from '@/utilities/composables/useMoveToSprint';
import { CardPopupRef } from '@/types/cardPopupRefs';
import { useConfirmDialog } from '@vueuse/core';
import ConfirmationModal from '@/components/widgets/ConfirmationModal';
import { Portal } from 'portal-vue';
import { getCardStatus } from '@/utilities/defaultCardStatuses';
import DateTimePicker from '@/components/widgets/DateTimePicker.vue';
import useCurrentRole from '@/utilities/composables/useCurrentRole';
import { cmdClickOpenInNewTab } from '@/utilities/openInNewTab';

export default {
  name: 'BoardListCard',
  components: {
    CardTag,
    AutoExpandTextarea,
    ChildCardsPreview,
    ChildCardsPreviewButton,
    CardDate,
    CardTags,
    CardMembers,
    CardProject,
    CardSubCards,
    CardActionsButton,
    ToggleExpandButton,
    DragHandle,
    DateTimePicker,
    DueDateTooltip,
    CurrentModelMembers,
    CardDateContent,
    ParentCardTitlePreview,
    CardCustomSection,
    ChildBranchIcons,
    ResourceIcon,
    ActionButtons,
    RelationsMeta,
    Portal,
    ConfirmationModal,
    CardTagsPopupWrapper,
    TagsPopoverContent,
    MembersDropdownList,
    ListCardStatusPicker,
    ListDropdown,
    BoardListCardActions,
  },
  mixins: [
    dueDateMixin,
    boardListCardMixin,
    filesReadUpload,
    isEditable,
    customIconsMixin,
    elementFocusMixin,
    cardStatuses,
    childCardCounterMixin,
    estimateOptions,
    moveCardMixin,
  ],

  provide() {
    return {
      currentCardId: this.cardId,
    };
  },
  props: {
    cardId: {
      type: String,
      required: true,
    },
    parentCardId: {
      type: String,
      required: false,
      default: '',
    },
    ancestorsCardIds: {
      type: Array,
      required: false,
      default: () => [],
    },
    index: {
      type: Number,
      required: true,
      default: 0,
    },
    lastItem: {
      type: Boolean,
      default: false,
    },
    depthSiblings: {
      type: Array,
      default: () => [],
    },
    largeBoard: {
      type: Boolean,
      required: false,
      default: false,
    },
    depth: {
      type: Number,
      default: 0,
    },
    layout: {
      type: String,
      default: '',
    },
    shiftHoverCardPreview: {
      type: Boolean,
      required: false,
      default: false,
    },
    listId: {
      type: [String, Number],
      required: false,
      default: '',
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: false,
    },

    isTemplate: {
      type: Boolean,
      required: false,
      default: false,
    },
    listCards: {
      type: Array,
      required: false,
      default: () => [],
    },

    forceHideDragHandle: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['scrollCard', 'cardMouseOver'],

  setup(props) {
    const _this = getCurrentInstance();

    const cardHover = ref(false);

    const { embeddedBoardAttributes, embed, inEpicBoard } = useBoardEmbed();
    const { handleToggleMember } = useManageCardMembers();
    const { priorityOptions } = usePriorityOptions();

    const currentCard = computed(() => _this.proxy?.$store.getters.getCardById(props.cardId) || {});

    const { cancelledOrCompletedSprint } = useCheckSprintState(currentCard);

    const selectedBoardId = computed(() =>
      currentCard.value.sprint_id
        ? `${currentCard.value.project_id}s${currentCard.value.sprint_id}`
        : currentCard.value.board_id
    );

    const listIds = computed(
      () => _this.proxy?.$store.getters.boardsListsIds[selectedBoardId.value] || []
    );

    const { boardLists } = useBoardLists(listIds);

    const { sprintOptions, sortedSpaceSprints } = useSprintPicker(currentCard);

    const { moveToSprint } = useMoveToSprint();

    const moveCardToCompletedSprintDialog = useConfirmDialog();
    const { isGuestReader } = useCurrentRole();

    return {
      embeddedBoardAttributes,
      embed,
      inEpicBoard,
      handleToggleMember,
      LinkedCardType,
      cardHover,
      priorityOptions,
      cancelledOrCompletedSprint,
      lists: boardLists,
      sprintPickerOptions: sprintOptions,
      sortedSpaceSprints,
      moveToSprint,
      CardPopupRef,
      moveCardToCompletedSprintDialog,
      bodyRef: document.body,
      ResourceType,
      isGuestReader,
    };
  },

  data() {
    return {
      memberPopupShown: false,
      rightPosition: 0,
      topPosition: 0,
      leftPosition: 0,
      cardHeight: 0,
      showChildCards: false,
      filesToDisplay: [],
      filesToUpload: [],
      uploadWatcher: null,
      draggingFile: false,
      tempTitle: '',
      isChildCardsShown: false,
      cardAttachmentsLoaded: false,
      dragEnterFired: false,
      visible: this.index < 8,
      cardMembersClicked: false,
      cardHeightTimeout: null,
      showEditTitle: false,
      dropdownItemClicked: false,
      priorityWrapShow: false,
      estimateWrapShow: false,
      showTagsTooltip: false,
      maxTagsForShow: 3,
      tagsTooltip: null,
      statusPickerOpen: false,
      shouldUseMainStatusPicker: true,
      sprintPickerOpen: false,
      dueDatePopupShown: false,
      startDatePopupShown: false,
      renderTagsTooltip: false,
    };
  },

  computed: {
    isCardAddedToEpic() {
      return Object.keys(this.card?.epic || {}).length;
    },
    epicsTitle() {
      return this.card.epic?.title || '';
    },
    epicsIcon() {
      const cardsEpicId = this.card.epic?.id || '';
      const stateEpicIcon = this.cards[cardsEpicId]?.icon;
      return stateEpicIcon || this.card.epic?.icon || {};
    },
    showParentCardTitlePreview() {
      return (
        (this.hasParentCard && this.isLayoutDefault && !this.isEpic) ||
        (this.inEpicBoard && this.isLayoutDefault)
      );
    },
    isEpic() {
      return this.card.type === ResourceType.Epic;
    },
    cardsProjectHasEstimateSystem() {
      return Boolean(this.getSelectedEstimateSystem(this.card.project_id));
    },
    isBoardOrEpicsBoard() {
      return (
        [this.$constants.routeNames.board, this.$constants.epicsBoardRoute.name].includes(
          this.$route.name
        ) || this.embed
      );
    },
    currentUserId() {
      return this.$store.getters.getUserId;
    },
    metaDataSections() {
      const sections = [
        {
          keyword: 'childCards',
          icon: 'card-child',
          value: this.childCardsCount,
          show:
            this.hasChildCards &&
            this.isLayoutList &&
            !this.shiftHoverCardPreview &&
            this.notListLayoutOnMobile &&
            !this.isLayoutTimeline,
        },
        {
          keyword: 'externalLinks',
          icon: 'git-pull-request',
          value: this.pullRequests,
          extraClass: this.externalLinksClass,
          show: this.hasExternalLinks && this.notListLayoutOnMobile && !this.isLayoutTimeline,
        },
        {
          keyword: 'checklists',
          icon: 'check-square',
          value: `${this.numberOfChecked}/${this.totalNumberOfChecks}`,
          show: this.totalNumberOfChecks && this.notListLayoutOnMobile && !this.isLayoutTimeline,
        },
        {
          keyword: 'files',
          icon: 'paperclip',
          value: this.filesCount,
          show: this.filesCount && this.notListLayoutOnMobile && !this.isLayoutTimeline,
        },
        {
          keyword: 'comments',
          icon: 'message-square',
          value: this.commentsCount,
          show: this.commentsCount && this.notListLayoutOnMobile && !this.isLayoutTimeline,
        },
      ];
      return sections.filter((section) => section.show);
    },

    hasNoPriority() {
      return this.card.priority === 0 || !this.card.priority;
    },
    hasNoEstimate() {
      return this.card.estimate === 0 || !this.card.estimate;
    },
    estimateSectionDisabled() {
      if (this.inEpicBoard) return !this.cardsProjectHasEstimateSystem;

      return (
        ([
          this.$constants.routeNames.view,
          this.$constants.routeNames.newView,
          this.$constants.myWorkRouteName,
          this.$constants.memberRouteName,
        ].includes(this.$route.name) &&
          !this.cardsProjectHasEstimateSystem) ||
        this.readOnly
      );
    },

    showPrioritySection() {
      return this.isLayoutList || !this.hasNoPriority || this.priorityWrapShow;
    },

    showEstimateSection() {
      // whether dropdown for estimate selection is shown or being shown
      const dropdownInteracted = this.estimateWrapShow;

      // board with list layout -> show estimate if cards's project has estimate system enabled
      // view-alike routes who use list layout -> always show estimate but disable it with estimateSectionDisabled
      const showWhenListLayout =
        this.isLayoutList &&
        (this.isBoardOrEpicsBoard && !this.inEpicBoard ? this.cardsProjectHasEstimateSystem : true);

      // board with board layout -> show estimate if card has estimate set and cards's project has estimate system enabled
      const showWhenBoardLayout =
        this.isLayoutDefault && this.cardsProjectHasEstimateSystem && !this.hasNoEstimate;

      return dropdownInteracted || showWhenListLayout || showWhenBoardLayout;
    },
    dueDateText() {
      if (this.isDueDateSet) {
        const dueDate = new Date(this.card.due_date * 1000);
        const dateLabel = this.monthsLabel[dueDate.getMonth()];
        return `${dueDate.getDate()} ${dateLabel.slice(0, 3)}`;
      }
      return '';
    },
    isViewOrMyWork() {
      return (
        (this.isViewOpen ||
          [this.$constants.myWorkRouteName, this.$constants.memberRouteName].includes(
            this.$route.name
          )) &&
        !this.inEpicBoard
      );
    },
    cardIdActionDropdown() {
      return this.$store.getters.getCurrentCardIdActionEdit;
    },
    totalNumberOfChecks() {
      if (this.localOnlyCard || !this.visible) return 0;
      const checklists = this.card.checklists || [];
      let count = 0;
      checklists.forEach((cl) => {
        count += (cl.items || []).length;
      });
      return count;
    },
    isCardForEdit() {
      return (
        this.cardIdActionDropdown === this.card.id &&
        this.getCurrentGroupByListIdCardActionEdit === this.listId &&
        this.$store.getters.getCurrentCardUniqueRowId === this.uniqueRowId
      );
    },
    isBoard() {
      return this.$route.name === 'board';
    },
    mouseSuspended() {
      return this.$store.getters.boardViewMouseSuspended;
    },
    attachmentTargetCardId() {
      return this.$store.getters.getAttachmentTargetCardId;
    },
    showTags() {
      return this.cardTags.length > 0;
    },
    monthsLabel() {
      return this.translate('months').split(',');
    },
    sortedMembers() {
      if (!this.visible) return [];
      return this.card.members
        ? [...this.card.members].sort(
            (a, b) => a.assigned_date - b.assigned_date || a.user_id?.localeCompare(b.user_id)
          )
        : this.card.members;
    },

    cards() {
      return this.$store.getters.getCards;
    },

    card() {
      return this.cards[this.cardId] || {};
    },

    externalLinks() {
      return this.card.external_links || [];
    },

    hasExternalLinks() {
      return this.externalLinks.length > 0;
    },

    numberOfExternalLinks() {
      return this.externalLinks.length;
    },

    pullRequests() {
      const mergedAndClosedStates = this.externalLinks.map(
        (link) => link[link.type].merged || link[link.type].state === 'closed'
      );
      const totalPRs = mergedAndClosedStates.length;

      if (!mergedAndClosedStates.includes(true)) {
        return totalPRs;
      }

      const mergedAndClosedPRs = mergedAndClosedStates.reduce(
        (acc, val) => (val === true ? acc + val : acc),
        0
      );
      return mergedAndClosedPRs === totalPRs ? totalPRs : `${mergedAndClosedPRs}/${totalPRs}`;
    },

    pullRequestTooltipContent() {
      const states = this.getPRCountPerState();

      let content = '';
      for (const [state, count] of Object.entries(states)) {
        if (count === 0) continue;

        const prState = `prState${state.charAt(0).toUpperCase() + state.slice(1)}`;
        content += `${this.translate(prState, { count })}, `;
      }

      // Remove trailing whitespace and comma
      return content.slice(0, -2);
    },

    keybSelectedCard() {
      return this.selectedCardId === this.cardId;
    },

    selectedCardId() {
      return this.$store.getters.selectedCardId;
    },

    isSelectedCard() {
      return this.cardHover || this.keybSelectedCard;
    },

    externalLinksClass() {
      if (!this.hasExternalLinks) return '';

      const states = this.getPRCountPerState();
      if (states.open > 0) return 'opened';
      if (states.merged > 0) return 'merged';
      return 'closed';
    },

    childCardsTooltip() {
      return this.isChildCardsShown
        ? this.translate('hideChildCards')
        : this.translate('showChildCards');
    },

    textareaHeight() {
      // line height of two rows
      return convertStyleToNumber(styleVariables.cardTitleLineHeight) * 2;
    },
    childStyleClasses() {
      return 'border-dividers-default hover:bg-important-hover';
    },
    isChild() {
      return !!this.parentCardId;
    },
    cardsLayout() {
      return this.embed
        ? this.embeddedBoardAttributes?.layout ||
            this.$store.getters.cardsLayout(this.embeddedBoardAttributes?.boardId)
        : this.$store.getters.cardsLayout();
    },
    isLayoutList() {
      return (this.layout || this.cardsLayout) === 'list' || !!this.setLayoutListForViewsAndMyWork;
    },
    setLayoutListForViewsAndMyWork() {
      return (
        [
          'view',
          'views',
          'newview',
          this.$constants.myWorkRouteName,
          this.$constants.memberRouteName,
        ].includes(this.$route.name) && !this.inEpicBoard
      );
    },
    isLayoutTimeline() {
      return (this.layout || this.cardsLayout) === 'timeline';
    },
    isLayoutListOrTimeline() {
      return this.isLayoutList || this.isLayoutTimeline;
    },
    isLayoutDefault() {
      return !this.isLayoutList && !this.isLayoutTimeline;
    },
    isCardDragging() {
      return (
        this.draggedEntity.cardId === this.card.id && this.draggedEntity.listId === this.listId
      );
    },
    listIcon() {
      if (this.card.sprint_id || this.inEpicBoard) {
        return this.cardStatuses[this.card.status]?.icon || this.cardStatuses.backlog.icon;
      }
      const isEpic = this.$route.name === this.$constants.epicsBoardRoute.name;
      return (
        (getCardStatus(this.cardList.behavior, isEpic) || {}).icon ||
        this.cardList.icon ||
        this.cardStatuses.backlog.icon ||
        ''
      );
    },
    listIconColor() {
      if (this.card.sprint_id || this.inEpicBoard) {
        return this.cardStatuses[this.card.status]?.color || this.cardStatuses.backlog.color;
      }
      return (
        this.cardList.color ||
        (this.cardStatuses[this.cardList.behavior] || {}).color ||
        this.cardStatuses.backlog.color ||
        ''
      );
    },
    listIconStyle() {
      return {
        ...this.getCustomIconStyle(this.iconPath(this.listIcon), {
          backgroundColor: this.listIconColor,
          webkitMaskSize: '16px 16px',
          maskSize: '16px 16px',
          maskPosition: 'center',
        }),
      };
    },
    cardStatusLabel() {
      return this.cardStatuses[this.cardList.behavior]?.label || this.cardStatuses.backlog.label;
    },
    showDragHandle() {
      return !this.cardHover || this.isChild || this.showEditTitle || this.isViewOrMyWork;
    },

    displayCardIcons() {
      return this.isLayoutListOrTimeline && !this.isCardDragging && !this.shiftHoverCardPreview;
    },

    parentCard() {
      return this.card.parent_card || {};
    },

    hasParentCard() {
      return Object.keys(this.parentCard).length;
    },

    getCurrentGroupByListIdCardActionEdit() {
      return this.$store.getters.getCurrentGroupByListIdCardActionEdit;
    },

    groupByStatus() {
      const groupByStatusArr = [groupByOption.status, groupByOption.statusType];
      if (this.embed && !this.inEpicBoard) {
        return groupByStatusArr.includes(this.embeddedBoardAttributes?.groupBy);
      }

      const boardId = this.inEpicBoard ? this.embeddedBoardAttributes.boardId : this.card.board_id;
      return groupByStatusArr.includes(this.$store.getters.getBoardGroupBy(boardId));
    },

    cardMemberIdForEdit() {
      return this.$store.getters.getCurrentCardIdMemberDropdown;
    },

    notListLayoutOnMobile() {
      return !(this.isMobile && this.isLayoutList);
    },

    readOnlyAllowDragCards() {
      return this.embeddedBoardAttributes?.allowDragCard || false;
    },

    isBlockedByOtherCard() {
      return this.card?.linked_cards?.some((c) => c.linked_card_type === LinkedCardType.BlockedBy);
    },

    blockedByCards() {
      return (
        this.card?.linked_cards?.filter((c) => c.linked_card_type === LinkedCardType.BlockedBy) ||
        []
      );
    },

    isBlockingOtherCard() {
      return this.card?.linked_cards?.some((c) => c.linked_card_type === LinkedCardType.Blocks);
    },

    blockingCards() {
      return (
        this.card?.linked_cards?.filter((c) => c.linked_card_type === LinkedCardType.Blocks) || []
      );
    },
    sprintSidebar() {
      return this.embeddedBoardAttributes?.sprintSidebar || false;
    },

    selectedStatus() {
      const listId = this.card?.sprint_id
        ? getSprintListKey(this.card.project_id, this.card.sprint_id, this.card?.list_id)
        : this.card?.list_id;

      const selectedStatus = this.lists?.find((l) => l.list_id === listId) || {};
      return { ...selectedStatus, listId: selectedStatus.list_id };
    },

    showMainStatusPicker() {
      return (
        (this.isSelectedCard || this.keybSelectedCard || this.statusPickerOpen) &&
        this.shouldUseMainStatusPicker
      );
    },
    selectedCardSprint() {
      return this.sortedSpaceSprints.findIndex(
        (sprint) => sprint.id.split('s')[1] === this.card?.sprint_id
      );
    },
    cardProject() {
      return this.$store.getters.getProjectById(this.card.project_id) || {};
    },
    projectAndBoardPreviewText() {
      const sprintKey = getSprintKey(this.card.project_id, this.card.sprint_id);

      const board = this.card.sprint_id
        ? this.$store.getters.getSpaceSprints(this.card.project_id)?.[sprintKey]
        : this.$store.getters.getBoardById(this.card.board_id);

      let boardTitle = this.card.board_title;
      if (this.card.sprint_id && !boardTitle) {
        boardTitle = `${ResourceType.Sprint} ${this.card.sprint_id}`;
      }

      return `${this.cardProject?.title || ''} / ${boardTitle || board?.title || ''}`;
    },
    showCardProject() {
      return (this.isViewOrMyWork && !this.isMobile) || (this.isLayoutList && this.inEpicBoard);
    },
    parentCardTitlePreviewText() {
      return this.inEpicBoard ? this.projectAndBoardPreviewText : this.parentCard.title;
    },
    parentCardTitlePreviewIcon() {
      return this.inEpicBoard ? this.cardProject.icon : {};
    },
    parentCardTitlePreviewTooltip() {
      return this.inEpicBoard ? this.projectAndBoardPreviewText : '';
    },
    currentCardMembers() {
      return this.$store.getters.getCurrentCardMembersDropdown?.members || [];
    },
  },
  watch: {
    cardDragging(newVal) {
      if (newVal && this.showEditTitle) {
        this.hideEditMode();
      }
    },
    cardIdActionDropdown(newValue) {
      if (newValue !== this.card.id) {
        this.cardHover = false;
      }
    },
    attachmentTargetCardId(newValue) {
      if (newValue !== this.card.id) {
        this.draggingFile = false;
      }
    },
    visible(newValue) {
      if (newValue) {
        this.cardHeightTimeout = setTimeout(() => {
          this.getCardHeight();
        }, 100);
      }
    },
    showEditTitle(newValue) {
      if (newValue) {
        this.$refs[`list-card-${this.card.id}`].addEventListener(
          'mouseup',
          this.blurElementHandler
        );
        return;
      }
      this.$refs[`list-card-${this.card.id}`].removeEventListener(
        'mouseup',
        this.blurElementHandler
      );
    },
    keybSelectedCard(newValue) {
      if (newValue) {
        const { vertical, deltaV } = this.checkIsInViewport();
        if (!vertical) {
          this.$emit('scrollCard', { dy: deltaV, speed: 0 });
        }
      }
    },

    cardTags() {
      // Remove tags tooltip instance on add or remove tag
      this.setShowTagsTooltip();
    },

    cardsLayout(newValue) {
      if (newValue === 'board') {
        this.setShowTagsTooltip();
      }
    },

    groupByStatus: {
      handler(newValue) {
        if (newValue && !this.isViewOrMyWork) this.shouldUseMainStatusPicker = true;
        else this.shouldUseMainStatusPicker = false;
      },
      immediate: true,
    },
  },

  created() {
    this.registerWatcherForCardsInEpic();
  },

  mounted() {
    if (this.visible) {
      this.cardHeightTimeout = setTimeout(() => {
        this.getCardHeight();
      }, 100);
    }
    this.tempTitle = this.card.title;
    EventBus.$on('card:open', this.openCard);
    EventBus.$on('card:checkIsInViewport', this.checkIsInViewportMesageHandler);
    EventBus.$on('card:rename', this.handleRenameMessage);
    EventBus.$on('card:openStatus', this.handleOpenStatusMessage);
    EventBus.$on('card:openAssignees', this.handleOpenAssigneesMessage);
    EventBus.$on('card:openPriority', this.handleOpenPriorityMessage);
    EventBus.$on('card:openEstimate', this.handleOpenEstimateMessage);
    EventBus.$on('card:openChildCardsMessage', this.handleOpenChildCardsMessage);
    EventBus.$on('card:openDueDate', this.handleOpenDueDateMessage);
    EventBus.$on('card:openTags', this.handleOpenTagsMessage);
    EventBus.$on('card:openStartDate', this.handleOpenStartDateMessage);
    EventBus.$on('card:toggleMyself', this.handleToggleMyselfMessage);
    EventBus.$on('card:addToSprint', this.handleAddToSprintMessage);
    if (this.cardsLayout === 'board') {
      this.setShowTagsTooltip();
    }
    window.addEventListener('popstate', this.handlePopstate);
  },
  unmounted() {
    EventBus.$off('card:open', this.openCard);
    EventBus.$off('card:checkIsInViewport', this.checkIsInViewportMesageHandler);
    EventBus.$off('card:rename', this.handleRenameMessage);
    EventBus.$off('card:openStatus', this.handleOpenStatusMessage);
    EventBus.$off('card:openPriority', this.handleOpenPriorityMessage);
    EventBus.$off('card:openEstimate', this.handleOpenEstimateMessage);
    EventBus.$off('card:openAssignees', this.handleOpenAssigneesMessage);
    EventBus.$off('card:openChildCardsMessage', this.handleOpenChildCardsMessage);
    EventBus.$off('card:openDueDate', this.handleOpenDueDateMessage);
    EventBus.$off('card:openTags', this.handleOpenTagsMessage);
    EventBus.$off('card:openStartDate', this.handleOpenStartDateMessage);
    EventBus.$off('card:toggleMyself', this.handleToggleMyselfMessage);
    clearTimeout(this.cardHeightTimeout);
    window.removeEventListener('popstate', this.handlePopstate);
  },
  methods: {
    getActionsButtonReferenceClientRect() {
      return this.$refs.actionsButton?.$el?.getBoundingClientRect?.();
    },
    tagsTooltipTarget() {
      return this.cardsLayout === 'list' || this.isViewOrMyWork
        ? this.$refs.tagsWrap?.$el
        : this.$refs.tagsSectionWrap;
    },
    checkIsBoardFullyLoaded(id, isSprint) {
      const boardOrSprint = this.$store.getters.getFlatBoards[id] || {};
      return this.$isBoardFullyLoaded(boardOrSprint, isSprint);
    },
    // register a watcher to fetch card when its selected in 'in-epic' board
    registerWatcherForCardsInEpic() {
      const isSprint = !!this.card.sprint_id;
      const boardOrSprintId = isSprint
        ? getSprintKey(this.card.project_id, this.card.sprint_id)
        : this.card.board_id;

      if (!this.inEpicBoard || this.checkIsBoardFullyLoaded(boardOrSprintId, isSprint)) return;

      const fetchFn = () => {
        if (isSprint) {
          this.$store.dispatch('getSprint', {
            sprintId: this.card.sprint_id,
            projectId: this.card.project_id,
          });
        } else {
          this.$store.dispatch('fetchBoard', { boardId: this.card.board_id });
        }
      };

      const fetchBoardWatcher = this.$watch('isSelectedCard', () => {
        // check if board was fetched through another source in meantime
        if (this.checkIsBoardFullyLoaded(boardOrSprintId, isSprint)) {
          fetchBoardWatcher();
          return;
        }

        fetchFn();
        fetchBoardWatcher();
      });
    },
    cardClickHandler(e) {
      e.preventDefault();
      if (cmdClickOpenInNewTab(this.cardLink, e)) return;
      this.cardClick();
      this.cardHover = false;
    },
    getCardHeight() {
      this.$nextTick(() => {
        this.cardHeight = this.$refs[`list-card-${this.cardId}`]?.clientHeight;
        clearTimeout(this.cardHeightTimeout);
      });
    },
    closeEditDesc() {
      this.hideEditMode();
    },
    handleDragover(event) {
      this.draggingFile = true;
      event.preventDefault();
    },

    handleDrop(event) {
      if (!this.draggingFile) return;
      this.draggingFile = false;
      event.preventDefault();

      const array = [...event.dataTransfer.items];

      const filesPromises = array.map((file) => {
        const fileF = file.getAsFile();
        return this.readFile(fileF);
      });

      Promise.allSettled(filesPromises).then(() => {
        if (this.cardAttachmentsLoaded) {
          this.insertFile(this.card.id);
        } else {
          this.uploadWatcher = this.$watch('cardAttachmentsLoaded', (newValue) => {
            if (newValue) {
              this.insertFile(this.card.id);
              this.uploadWatcher();
            }
          });
        }
      });
    },

    loadFiles() {
      return this.$store.dispatch('getFiles', { card_id: this.card.id });
    },

    handleDragEnter(event) {
      if (this.dragEnterFired) {
        this.dragEnterFired = false;
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.dragEnterFired = true;
      const dt = event.dataTransfer;

      if (dt.types && dt.types.includes('Files')) {
        this.draggingFile = true;
        this.$store.dispatch('setAttachmentTargetCardId', this.card.id);
        this.loadFiles().then(() => {
          this.cardAttachmentsLoaded = true;
        });
        this.$store.dispatch('setCardAttachmentsLoaded', true);
      }
    },
    handleDragLeave() {
      if (this.draggingFile) {
        this.draggingFile = false;
      }
    },
    onToggleChildCards() {
      this.isChildCardsShown = !this.isChildCardsShown;
    },
    visibilityChanged(visible) {
      if (!visible && !this.largeBoard) return;
      if (this.draggedEntity.cardId === this.card.id) return;
      this.visible = visible;
    },
    mouseMoveHandler() {
      if (this.draggingIsOn || this.mouseSuspended || this.isMobile) return;
      if (
        this.$store.getters.lastHoveredCardId === this.card.id &&
        this.$store.getters.lastHoveredListId === this.listId
      ) {
        return;
      }
      this.$emit('cardMouseOver', { cardId: this.card.id, listId: this.listId });
    },
    mouseLeaveHandler() {
      if (this.draggingIsOn) return;
      if (
        this.uniqueRowId !== this.$store.getters.getCurrentCardUniqueRowId &&
        !this.estimateWrapShow
      ) {
        this.cardHover = false;
      }
      this.renderTagsTooltip = false;
    },
    mouseOverHandler(event) {
      if (this.mouseSuspended) return;
      if (this.isMobile) return;
      if (this.cardDragging) return;
      event.stopPropagation();
      this.cardHover = true;

      if (
        this.cardTags.length > this.maxTagsForShow ||
        this.showTagsTooltip ||
        (this.isLayoutList && this.cardTags.length > 0)
      ) {
        if (this.tagsTooltip) return;
        this.renderTagsTooltip = true;
      }
    },
    dragOverHandler(event) {
      if (this.cardDragging) return;
      event.stopPropagation();
      event.preventDefault();
    },
    checkIsInViewportMesageHandler(cardId) {
      if (this.card.id === cardId) {
        this.$nextTick(() => {
          const { vertical, deltaV } = this.checkIsInViewport();
          if (!vertical) {
            this.$emit('scrollCard', { dy: deltaV, speed: 0 });
          }
        });
      }
    },
    checkIsInViewport() {
      // use listId from param, in grouped by board card list_id is not correct
      const listElement = document.querySelector(`.board-column[data-list-id="${this.listId}"]`);
      if (!listElement) return { vertical: false, deltaV: 0 };
      const headerHeight = parseInt(styleVariables.columnHeaderHeight, 10);
      const footerHeight = parseInt(styleVariables.boardNewCardHeightWithPadding, 10);
      const cardNode = this.$refs[`list-card-${this.card.id}`];
      if (cardNode) {
        return isInViewport(cardNode, listElement, {
          // 3px is the outline of the higlighted card, 4px is the card margin
          top: headerHeight + 3 + 4,
          bottom: footerHeight + 3 + 4,
          right: 0,
          left: 0,
        });
      }
      return { vertical: false, deltaV: 0 };
    },
    getTagsReferenceClientRect() {
      if (this.isLayoutListOrTimeline || this.isViewOrMyWork) {
        return this.$refs.tagsWrap?.$el?.getBoundingClientRect?.();
      }
      return this.$refs.tagsWrapPopover?.$el?.getBoundingClientRect?.();
    },
    handleRenameMessage(payload) {
      if (this.card.id === payload.id) {
        if (!this.keybSelectedCard && !this.cardHover) return;
        this.tempTitle = this.card.title;
        this.showEditInput(payload);
      }
    },
    handleOpenMessage({ cardId, listId }, refName, toggleMethod) {
      if (this.card.id === cardId && (!listId || this.listId === listId)) {
        if (!this.keybSelectedCard && !this.cardHover) return;
        this.$nextTick(() => {
          const rect = this.$refs[refName]?.$el?.getBoundingClientRect?.();
          // pass emulated pointer event which is required by `getElementPositionByPointer` method
          const emulatedEvent = {
            x: rect.x,
            y: rect.y,
            offsetX: 0,
            offsetY: 0,
            target: this.$refs[refName].$el,
          };
          this[toggleMethod]({ e: emulatedEvent, cardId, listId });
        });
      }
    },
    handleOpenMessageByClick({ cardId }, refName, toggleMethod) {
      if (this.card.id === cardId) {
        const emulatedEvent = {
          stopPropagation: () => {},
        };
        this.$nextTick(() => {
          this.$refs[refName]?.[toggleMethod]?.(emulatedEvent);
        });
      }
    },

    priorityWrapDropdownChange(state) {
      this.priorityWrapShow = state;
    },

    estimateWrapDropdownChange(state) {
      this.estimateWrapShow = state && this.getEstimateOptions(this.card.project_id).length > 0;
    },
    handleOpenAssigneesMessage({ cardId, listId, paramUniqueRowId }) {
      if (
        this.readOnly ||
        this.card.id !== cardId ||
        (listId && this.listId !== listId) ||
        (paramUniqueRowId !== this.uniqueRowId && paramUniqueRowId)
      )
        return;

      this.$refs?.membersPopoverRef?.show();
      this.toggleCardMembersDropdownClick();
    },
    onMembersTrigger(_, e) {
      this.stopTriggerPropagation(_, e);
      this.toggleCardMembersDropdownClick();
    },
    onMembersShow() {
      if (this.readOnly) return false;
      this.memberPopupShown = true;
      return !(!this.isSelectedCard && !this.memberPopupShown);
    },
    onMembersHide() {
      this.memberPopupShown = false;
      this.$store.dispatch('setCurrentCardIdMemberDropdown', '');
    },

    handleOpenChildCardsMessage({ cardId, listId }) {
      this.handleOpenMessage({ cardId, listId }, 'childCardsWrap', 'onToggleChildCards');
    },
    handleOpenPriorityMessage({ cardId, paramUniqueRowId }) {
      if (this.card.id !== cardId) return;
      if (paramUniqueRowId === this.uniqueRowId) {
        this.priorityWrapShow = true;
        this.handleOpenMessageByClick({ cardId }, 'priorityWrap', 'toggleDropdown');
      }
    },
    handleOpenEstimateMessage({ cardId, paramUniqueRowId }) {
      if (this.card.id !== cardId) return;
      if (paramUniqueRowId === this.uniqueRowId) {
        this.estimateWrapShow = true;
        this.handleOpenMessageByClick({ cardId }, 'estimateWrap', 'toggleDropdown');
      }
    },
    handleOpenDueDateMessage({ cardId, listId, paramUniqueRowId }) {
      if (
        this.readOnly ||
        this.card.id !== cardId ||
        (this.listId !== listId && listId) ||
        (paramUniqueRowId !== this.uniqueRowId && paramUniqueRowId)
      ) {
        return;
      }

      if (this.isLayoutListOrTimeline) {
        this.$refs.dueDateWrap?.onToggleDueDatePopup();
        return;
      }

      this.dueDatePopupShown = true;

      this.$nextTick(() => {
        this.$refs.dueDateWrap.show();
      });
    },

    handleOpenStartDateMessage({ cardId, listId, paramUniqueRowId }) {
      if (
        this.readOnly ||
        this.card.id !== cardId ||
        (this.listId !== listId && listId) ||
        (paramUniqueRowId !== this.uniqueRowId && paramUniqueRowId)
      ) {
        return;
      }

      this.startDatePopupShown = true;
      this.$refs.startDateWrap.show();
    },

    handleToggleMyselfMessage({ cardId, listId }) {
      if (this.readOnly) return;
      if (this.card.id === cardId && this.listId === listId) {
        this.handleToggleMember({ cardId });
      }
    },

    handleOpenTagsMessage({ cardId, listId, paramUniqueRowId }) {
      if (
        this.card.id !== cardId ||
        (this.listId !== listId && listId) ||
        (paramUniqueRowId !== this.uniqueRowId && paramUniqueRowId)
      ) {
        return;
      }

      if (this.isLayoutListOrTimeline) {
        this.$refs.tagsWrap?.showPopup();
        return;
      }

      this.toggleCardTags();
    },

    toggleCardTags() {
      if (this.isMobile) {
        return;
      }

      this.hideEditMode();
      this.hideCardActionsDropdown();
      this.hideMemberDropdown();

      this.$refs.tagsWrapPopover?.show();
    },

    setShowTagsTooltip() {
      this.$nextTick(() => {
        for (const element of this.cardTags) {
          const tag = element;
          if (!this.$refs[`tagWrap-${tag.id}`]) return;
          const tagWrap = this.$refs[`tagWrap-${tag.id}`][0];
          if (tagWrap) {
            const tagWrapWidth = tagWrap.$el.clientWidth;
            if (
              tagWrapWidth >= getSmallCardTagMaxWidth() ||
              this.cardTags.length > this.maxTagsForShow
            ) {
              this.showTagsTooltip = true;
              break;
            } else {
              this.showTagsTooltip = false;
            }
          }
        }
      });
    },

    stopTriggerPropagation(i, event) {
      event.preventDefault();
      event.stopPropagation();
    },

    hideActionsDropdown() {
      this.cardHover = false;
      this.$store.dispatch('setCurrentCardUniqueRowId', '');
    },

    getTooltipIconSection(keyword) {
      return keyword === 'externalLinks'
        ? {
            content: this.pullRequestTooltipContent,
            placement: 'bottom',
            onShow: () => !this.isMobile,
          }
        : {};
    },

    getPRCountPerState() {
      const states = {
        open: 0,
        closed: 0,
        merged: 0,
      };

      this.externalLinks.forEach((link) => {
        const state = link[link.type].state;
        const isMerged = link[link.type].merged;

        states[state]++;
        states.merged += Number(isMerged);
      });

      // Subtract from closed because all merged states are closed by default
      states.closed -= states.merged;

      return states;
    },

    handleOpenStatusMessage({ cardId, listId, uniqueRowId }) {
      if (
        this.card.id !== cardId ||
        (this.listId !== listId && listId) ||
        (uniqueRowId !== this.uniqueRowId && uniqueRowId)
      )
        return;

      this.statusPickerOpen = true;

      this.$nextTick(() => {
        this.$refs[CardPopupRef.Status]?.show?.();
      });
    },

    actionsButtonStateChange(state) {
      if (state.isVisible) {
        this.showCustomSections = true;
      }
    },

    statusPickerStateChange(state) {
      this.statusPickerOpen = state.isVisible;
    },

    sprintPickerStateChange(state) {
      this.sprintPickerOpen = state.isVisible;
    },

    handleStatusItemClick(status, hideFn) {
      if (typeof hideFn === 'function') {
        hideFn();
      }
      if (status.list_id !== this.card?.list_id) {
        this.moveCardToDifferentList(status.list_id, this.card.id);
      }
      this.statusPickerOpen = false;
    },

    handleAddToSprintMessage({ cardId, uniqueRowId }) {
      if (this.card.id !== cardId || (uniqueRowId !== this.uniqueRowId && uniqueRowId)) return;

      this.sprintPickerOpen = true;
      this.$refs[CardPopupRef.Sprint]?.show?.();
    },

    async handleSprintItemClick(sprint, hideFn) {
      if (typeof hideFn === 'function') {
        hideFn();
      }

      if (sprint.state === SprintState.Completed && this.card?.status !== ListBehavior.Completed) {
        const { isCanceled } = await this.moveCardToCompletedSprintDialog.reveal();
        if (isCanceled) return;
      }

      this.moveToSprint(sprint.id, this.card, true);
    },
  },
};
</script>

<style lang="scss">
.board-list-card-wrap {
  border: 1px solid;
  position: relative;
  border-radius: 8px;
  box-sizing: border-box;
  overflow: hidden;
  @apply bg-emphasised-bg border-emphasised-border text-text-default block;

  .parent-card {
    max-width: calc(100% - 32px);
    @apply bg-transparent border-none p-0 ml-4;
    .parent-card-title {
      @apply text-inactive-text leading-snug;
    }
  }

  &.drop-target {
    border: 1px solid #929292;
  }
  &.cardHover {
    @apply bg-emphasised-hover;
  }
  &.list-card-drop {
    border: none;
    .card-item-separator {
      display: none;
    }
  }
  &.dragging:hover:not(.mobile *) {
    @apply bg-important-bg;
  }

  .list-card {
    display: flex;
    flex-direction: column;
    flex: 1;
    z-index: 1;
    .members-wrap {
      display: flex;
      flex-direction: row;
      z-index: 1;
      align-items: flex-start;
      margin-right: 3px;

      .board-list-card-members {
        margin-right: 18px;
        .member-avatar {
          .avatar {
            width: 16px;
            height: 16px;
          }
        }
      }
      &.hover {
        z-index: unset;
      }
    }
    @import '@/assets/scss/preview-card.scss';

    .owner-tags-wrap {
      border: 1px solid $gray200;
      border-radius: 4px;
      margin: 14px 0 0 14px;
      padding: 2px;
      max-width: fit-content;
      min-height: 16px;

      .tags-list {
        display: flex;
      }
    }
  }

  .board-list-card-drop-target {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 11;
    @apply bg-hover-onHover;

    &.small-target {
      flex-direction: row-reverse;
      span {
        margin-top: 0;
      }
      em {
        margin-left: 16px;
      }
    }

    em.custom-icons {
      @apply w-8 h-8 pointer-events-none;
      background-color: #52ad6f;
    }

    span {
      margin-top: 8px;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.3px;
      font-weight: 500;
      pointer-events: none;

      @apply text-text-default;
    }
  }
  &.timeline-sidebar-item {
    .list-card {
      .tags-item {
        @apply w-[4.125rem] #{!important};
      }
    }
  }

  &__dragging-on {
    @apply bg-important-bg;
    cursor: default !important;
  }
}

[data-last-hovered='true']:not([data-selected-card='true']) {
  .board-list-card-wrap {
    @apply text-text-default;
    &:hover:not(.mobile, .dragging *) {
      @apply bg-emphasised-hover text-text-default;
    }
  }
}

[data-selected-card='true'] {
  .board-list-card-wrap,
  .list-row,
  .create-add-button {
    box-shadow: 0px 0px 0px 2px $primary100;
    @apply bg-important-hover text-text-default;
    &:hover:not(.mobile *) {
      @apply bg-important-hover text-text-default;
    }
  }
}

.due-date-text-small-card {
  background: transparent !important;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0.1px;
  margin-left: 4px;
  width: fit-content !important;
  @apply text-deEmphasised-text;
}

.small-card-tags-popover {
  @apply rounded-lg;
  .card-tag-item {
    @apply h-[18px];
    .card-tag-wrap .tag-content-wrap .tag-name {
      @apply text-xs leading-[18px] text-text-defaultTooltip;
    }
  }
  .tags-icon {
    @apply w-3 h-3 min-w-3 mr-0.5;
  }
}
</style>
